import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, Send, Plus, Copy, Check, Trash2, Loader2, Sparkles, Mail, Pencil, X as XIcon } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { CJ_AVATAR_URL } from "../lib/constants";

/* -------------------------- Message bubble ------------------------- */

function CopyButton({ text }) {
  const [copied, setCopied] = useState(false);
  const onClick = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success("Copied to clipboard");
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error("Couldn't copy");
    }
  };
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid="copilot-copy-message"
      className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-widest text-slate-500 hover:text-tc-ink transition-colors"
    >
      {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
      {copied ? "Copied" : "Copy"}
    </button>
  );
}

function MessageBubble({ msg }) {
  const isUser = msg.role === "user";
  const isEmptyStreaming = !isUser && msg.streaming && !msg.content;
  return (
    <div className={`flex gap-3 ${isUser ? "justify-end" : "justify-start"}`}>
      {!isUser && (
        <img src={CJ_AVATAR_URL} alt="CJ" className="w-9 h-9 rounded-full shrink-0 border-2 border-tc-cream object-cover" />
      )}
      <div className={`max-w-[720px] rounded-2xl px-4 py-3 ${
        isUser
          ? "bg-tc-ink text-white rounded-tr-sm"
          : "bg-white border border-slate-200 rounded-tl-sm shadow-sm"
      }`}>
        {isEmptyStreaming ? (
          <div className="flex items-center gap-2 text-sm text-slate-500">
            <span className="flex gap-1">
              <span className="w-1.5 h-1.5 rounded-full bg-tc-orange animate-bounce" style={{ animationDelay: "0ms" }} />
              <span className="w-1.5 h-1.5 rounded-full bg-tc-orange animate-bounce" style={{ animationDelay: "150ms" }} />
              <span className="w-1.5 h-1.5 rounded-full bg-tc-orange animate-bounce" style={{ animationDelay: "300ms" }} />
            </span>
            {msg.thinkingLabel || "CJ is drafting"}
            <span className="opacity-50">…</span>
          </div>
        ) : (
          <>
            <div
              className="whitespace-pre-wrap text-[15px] leading-relaxed break-words"
              style={{ color: isUser ? "#fff" : "#0F172A" }}
            >
              {msg.content}
              {!isUser && msg.streaming && (
                <span className="inline-block w-1.5 h-4 ml-0.5 bg-tc-orange align-middle animate-pulse" />
              )}
            </div>
            {!isUser && !msg.streaming && msg.content && msg.content.length > 60 && (
              <div className="mt-2 pt-2 border-t border-slate-100 flex justify-end">
                <CopyButton text={msg.content} />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

/* ----------------------------- Page -------------------------------- */

const SUGGESTIONS = [
  "Draft a cold outreach to an HR director at a mid-size manufacturer. Open to the half-day program.",
  "Write a follow-up email after a 30-min discovery call where they mentioned low team engagement.",
  "Help me respond to a client who said \"the price feels high.\"",
  "Rewrite this to sound warmer: Hi John, following up on the proposal. Let me know. Thanks.",
];

export default function EmailCopilot() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [sessions, setSessions] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [loadingSession, setLoadingSession] = useState(false);
  const [renamingId, setRenamingId] = useState(null);
  const [renameDraft, setRenameDraft] = useState("");
  const scrollRef = useRef(null);
  const textRef = useRef(null);
  const renameRef = useRef(null);

  // Initial load: fetch sessions, auto-open latest OR create new.
  // If a ?session=... deep-link is present (from Find Leads), prefer that.
  useEffect(() => {
    (async () => {
      try {
        const deepSession = searchParams.get("session");
        const { data } = await api.get("/email-copilot/sessions");
        const list = Array.isArray(data) ? data : [];
        if (deepSession && list.some((s) => s.id === deepSession)) {
          setSessions(list);
          setActiveId(deepSession);
        } else if (list.length > 0) {
          setSessions(list);
          setActiveId(list[0].id);
        } else {
          const { data: fresh } = await api.post("/email-copilot/sessions");
          setSessions([fresh]);
          setActiveId(fresh.id);
        }
      } catch {
        toast.error("Couldn't load Email Co-Pilot.");
      }
    })();
  }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  // Load messages when active session changes
  useEffect(() => {
    if (!activeId) return;
    (async () => {
      setLoadingSession(true);
      try {
        const { data } = await api.get(`/email-copilot/sessions/${activeId}`);
        setMessages(Array.isArray(data?.messages) ? data.messages : []);
      } catch {
        setMessages([]);
      } finally {
        setLoadingSession(false);
      }
    })();
  }, [activeId]);

  // If we were deep-linked here from Find Leads with a seed message AND the
  // active session has no messages yet, auto-send the seed so CJ starts
  // drafting on arrival.
  useEffect(() => {
    const seed = searchParams.get("seed");
    const deepSession = searchParams.get("session");
    if (!seed || !deepSession || deepSession !== activeId) return;
    if (loadingSession || messages.length > 0 || sending) return;
    // Clear the URL param so a refresh doesn't fire twice
    searchParams.delete("seed");
    searchParams.delete("session");
    setSearchParams(searchParams, { replace: true });
    sendMessage(seed);
  }, [activeId, loadingSession, messages.length, sending]);  // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-scroll on new messages
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, sending]);

  // Auto-resize textarea
  useEffect(() => {
    const ta = textRef.current;
    if (!ta) return;
    ta.style.height = "auto";
    ta.style.height = `${Math.min(ta.scrollHeight, 200)}px`;
  }, [input]);

  const sendMessage = async (contentOverride) => {
    const content = (contentOverride ?? input).trim();
    if (!content || sending || !activeId) return;
    setInput("");
    setSending(true);
    const nowIso = new Date().toISOString();
    const optimistic = { role: "user", content, created_at: nowIso };
    // Append the user message + an empty assistant placeholder we'll stream into.
    const assistantIdx = (prevLen) => prevLen + 1; // helpful for mental model
    setMessages((prev) => [
      ...prev,
      optimistic,
      { role: "assistant", content: "", created_at: nowIso, streaming: true },
    ]);

    const token = typeof window !== "undefined" ? localStorage.getItem("tc_token") : null;
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/email-copilot/sessions/${activeId}/stream`;
    try {
      const resp = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ content }),
      });
      if (!resp.ok || !resp.body) {
        const errText = await resp.text().catch(() => "");
        throw new Error(errText || `HTTP ${resp.status}`);
      }

      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let newTitle = null;
      // SSE frames are separated by a blank line. Parse incrementally.
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const frames = buffer.split("\n\n");
        buffer = frames.pop(); // last chunk may be partial
        for (const frame of frames) {
          const lines = frame.split("\n");
          let eventName = "message";
          let dataStr = "";
          for (const line of lines) {
            if (line.startsWith("event:")) eventName = line.slice(6).trim();
            else if (line.startsWith("data:")) dataStr = line.slice(5).trim();
          }
          if (!dataStr) continue;
          let data;
          try { data = JSON.parse(dataStr); } catch { continue; }
          if (eventName === "thinking" && data.label) {
            // Update the placeholder's `thinkingLabel` so the user sees stage progress.
            setMessages((prev) => {
              const next = [...prev];
              const last = next[next.length - 1];
              if (last && last.role === "assistant" && !last.content) {
                next[next.length - 1] = { ...last, thinkingLabel: data.label };
              }
              return next;
            });
          } else if (eventName === "delta") {
            setMessages((prev) => {
              const next = [...prev];
              const last = next[next.length - 1];
              if (last && last.role === "assistant") {
                next[next.length - 1] = { ...last, content: (last.content || "") + (data.text || ""), thinkingLabel: null };
              }
              return next;
            });
          } else if (eventName === "done") {
            newTitle = data.title;
            window.dispatchEvent(new Event("cj-ai-check"));
            setMessages((prev) => {
              const next = [...prev];
              const last = next[next.length - 1];
              if (last && last.role === "assistant") {
                next[next.length - 1] = { ...last, streaming: false, created_at: data.created_at || last.created_at };
              }
              return next;
            });
          }
        }
      }

      if (newTitle) {
        setSessions((prev) => prev.map((s) => s.id === activeId ? { ...s, title: newTitle, updated_at: nowIso } : s));
      }
    } catch (e) {
      toast.error(e?.message || "CJ hit a snag. Try again?");
      // Roll back the optimistic + placeholder messages
      setMessages((prev) => prev.slice(0, -2));
      setInput(content);
    } finally {
      setSending(false);
    }
  };

  const newChat = async () => {
    try {
      const { data } = await api.post("/email-copilot/sessions");
      setSessions((prev) => [data, ...prev]);
      setActiveId(data.id);
      setMessages([]);
      setInput("");
    } catch {
      toast.error("Couldn't start a new chat.");
    }
  };

  const deleteChat = async (id, e) => {
    e?.stopPropagation();
    if (!window.confirm("Delete this chat? This can't be undone.")) return;
    try {
      await api.delete(`/email-copilot/sessions/${id}`);
      setSessions((prev) => {
        const next = prev.filter((s) => s.id !== id);
        if (activeId === id) {
          if (next.length > 0) setActiveId(next[0].id);
          else newChat();
        }
        return next;
      });
    } catch {
      toast.error("Couldn't delete that chat.");
    }
  };

  const startRename = (session, e) => {
    e?.stopPropagation();
    setRenamingId(session.id);
    setRenameDraft(session.title || "");
    // Focus + select-all after state settles
    setTimeout(() => {
      renameRef.current?.focus();
      renameRef.current?.select();
    }, 10);
  };

  const cancelRename = () => {
    setRenamingId(null);
    setRenameDraft("");
  };

  const saveRename = async () => {
    const id = renamingId;
    const title = renameDraft.trim();
    if (!id) return;
    if (!title) { cancelRename(); return; }
    // Optimistic update
    setSessions((prev) => prev.map((s) => s.id === id ? { ...s, title } : s));
    setRenamingId(null);
    setRenameDraft("");
    try {
      await api.patch(`/email-copilot/sessions/${id}`, { title });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't rename that chat.");
      // Roll back by refetching one session row
      try {
        const { data } = await api.get(`/email-copilot/sessions/${id}`);
        setSessions((prev) => prev.map((s) => s.id === id ? { ...s, title: data.title } : s));
      } catch {}
    }
  };

  const onRenameKey = (e) => {
    if (e.key === "Enter") { e.preventDefault(); saveRename(); }
    else if (e.key === "Escape") { e.preventDefault(); cancelRename(); }
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const showEmptyState = !loadingSession && messages.length === 0;

  return (
    <motion.div
      className="max-w-7xl mx-auto"
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      data-testid="email-copilot-page"
    >
      <Link to="/" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-tc-ink mb-4" data-testid="copilot-back">
        <ArrowLeft className="w-4 h-4" /> Back to Hub
      </Link>

      <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-4 lg:gap-6">
        {/* ------------------------- Sidebar --------------------------- */}
        <aside className="lg:sticky lg:top-6 self-start">
          <button
            onClick={newChat}
            data-testid="copilot-new-chat"
            className="w-full h-11 rounded-xl bg-tc-ink text-white font-bold text-sm flex items-center justify-center gap-2 hover:bg-slate-800 transition-colors mb-3"
          >
            <Plus className="w-4 h-4" /> New chat
          </button>
          <div className="text-[11px] font-bold uppercase tracking-widest text-slate-400 px-2 mb-2">Recent chats</div>
          <div className="space-y-1 max-h-[70vh] overflow-y-auto pr-1" data-testid="copilot-session-list">
            {sessions.length === 0 && (
              <div className="text-xs text-slate-400 px-2">No chats yet.</div>
            )}
            {sessions.map((s) => {
              const isRenaming = renamingId === s.id;
              const isActive = s.id === activeId;
              return (
                <div
                  key={s.id}
                  onClick={() => !isRenaming && setActiveId(s.id)}
                  onDoubleClick={(e) => startRename(s, e)}
                  className={`group w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors cursor-pointer ${
                    isActive
                      ? "bg-tc-cream text-tc-ink font-semibold"
                      : "text-slate-600 hover:bg-slate-100"
                  }`}
                  data-testid={`copilot-session-row-${s.id}`}
                >
                  <Mail className="w-3.5 h-3.5 shrink-0 opacity-60" />
                  {isRenaming ? (
                    <>
                      <input
                        ref={renameRef}
                        type="text"
                        value={renameDraft}
                        onChange={(e) => setRenameDraft(e.target.value)}
                        onKeyDown={onRenameKey}
                        onBlur={saveRename}
                        onClick={(e) => e.stopPropagation()}
                        maxLength={120}
                        data-testid={`copilot-rename-input-${s.id}`}
                        className="flex-1 min-w-0 bg-white border border-tc-orange rounded px-1.5 py-0.5 text-sm outline-none text-tc-ink"
                      />
                      <Check
                        onClick={(e) => { e.stopPropagation(); saveRename(); }}
                        className="w-3.5 h-3.5 shrink-0 text-green-600 hover:scale-110 transition-transform"
                        data-testid={`copilot-rename-save-${s.id}`}
                      />
                      <XIcon
                        onClick={(e) => { e.stopPropagation(); cancelRename(); }}
                        className="w-3.5 h-3.5 shrink-0 text-slate-400 hover:text-slate-700 transition-colors"
                        data-testid={`copilot-rename-cancel-${s.id}`}
                      />
                    </>
                  ) : (
                    <>
                      <span className="truncate flex-1">{s.title || "New chat"}</span>
                      <Pencil
                        onClick={(e) => startRename(s, e)}
                        className="w-3.5 h-3.5 opacity-0 group-hover:opacity-60 hover:!opacity-100 hover:text-tc-orange transition-opacity shrink-0 cursor-pointer"
                        data-testid={`copilot-rename-${s.id}`}
                      />
                      <Trash2
                        onClick={(e) => deleteChat(s.id, e)}
                        className="w-3.5 h-3.5 opacity-0 group-hover:opacity-60 hover:!opacity-100 hover:text-red-500 transition-opacity shrink-0 cursor-pointer"
                        data-testid={`copilot-delete-${s.id}`}
                      />
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </aside>

        {/* ------------------------- Chat panel ------------------------- */}
        <section className="bg-tc-cream/40 rounded-3xl border border-slate-200 flex flex-col overflow-hidden" style={{ minHeight: "72vh" }}>
          {/* Header */}
          <div className="flex items-center gap-3 px-6 py-4 bg-white border-b border-slate-200">
            <img src={CJ_AVATAR_URL} alt="CJ" className="w-10 h-10 rounded-full border-2 border-tc-cream object-cover" />
            <div>
              <div className="font-black text-tc-ink flex items-center gap-2">
                Email Co-Pilot
                <Sparkles className="w-3.5 h-3.5" style={{ color: "#f68b1f" }} />
              </div>
              <div className="text-xs text-slate-500">Ask CJ to draft or refine any sales email. She knows the whole program catalog.</div>
            </div>
          </div>

          {/* Message list */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto p-6 space-y-4" data-testid="copilot-message-list">
            {showEmptyState && (
              <div className="h-full flex flex-col items-center justify-center text-center py-12">
                <img src={CJ_AVATAR_URL} alt="CJ" className="w-20 h-20 rounded-full border-4 border-white shadow-md mb-4" />
                <div className="text-xl font-black text-tc-ink mb-1">Hey — I've got you.</div>
                <div className="text-sm text-slate-500 max-w-md mb-6">
                  Tell me what you need. A cold intro, a reply to a tough question, a warmer rewrite — I'll draft it with the right True Colors program in mind.
                </div>
                <div className="w-full max-w-2xl grid grid-cols-1 md:grid-cols-2 gap-2">
                  {SUGGESTIONS.map((s, i) => (
                    <button
                      key={i}
                      onClick={() => sendMessage(s)}
                      className="text-left text-sm px-4 py-3 rounded-xl bg-white border border-slate-200 hover:border-tc-orange hover:shadow-sm transition-all"
                      data-testid={`copilot-suggestion-${i}`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}
            <AnimatePresence initial={false}>
              {messages.map((m, i) => (
                <motion.div
                  key={`${m.created_at}-${i}`}
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.2 }}
                >
                  <MessageBubble msg={m} />
                </motion.div>
              ))}
            </AnimatePresence>
          </div>

          {/* Composer */}
          <div className="border-t border-slate-200 bg-white p-4">
            <div className="relative">
              <textarea
                ref={textRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={onKeyDown}
                rows={1}
                placeholder="Ask CJ to draft an email, respond to one, or rewrite a message…"
                data-testid="copilot-input"
                className="w-full resize-none rounded-2xl border-2 border-slate-200 focus:border-tc-orange outline-none px-4 py-3 pr-14 text-[15px] leading-relaxed max-h-[200px]"
                disabled={sending}
              />
              <button
                onClick={() => sendMessage()}
                disabled={!input.trim() || sending}
                data-testid="copilot-send"
                className="absolute right-2 bottom-2 w-10 h-10 rounded-xl text-white flex items-center justify-center disabled:opacity-30 disabled:cursor-not-allowed hover:brightness-110 transition-all"
                style={{ background: "#f68b1f" }}
              >
                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              </button>
            </div>
            <div className="text-[11px] text-slate-400 mt-2 pl-1">
              Press <kbd className="px-1 py-0.5 rounded bg-slate-100 border border-slate-200">Enter</kbd> to send, <kbd className="px-1 py-0.5 rounded bg-slate-100 border border-slate-200">Shift</kbd> + <kbd className="px-1 py-0.5 rounded bg-slate-100 border border-slate-200">Enter</kbd> for a new line.
            </div>
          </div>
        </section>
      </div>
    </motion.div>
  );
}
